/**
 * IngresosView -- Ingresos (#/ingresos) espejo Desktop FrmIngresosV15.
 *
 * A-T3 (PANEL-FINANZAS-INGRESOS-EGRESOS-PARIDAD-V1-001): consume el modo
 * groupBy=venta del endpoint (API #82, live 76e486b):
 *   - POR VENTA (default): 1 fila por factura. Columnas espejo FrmIngresosV15:
 *     Factura# (venta_id) | Fecha | Medio | Total. medio='mixto' si la venta
 *     combina medios (num_pagos las desglosa). Sin expand (el agregado no tiene id).
 *   - POR PAGO: 1 fila por pago individual (auditoria). Expand = detalle del pago.
 *
 * Sin columnas Cliente/Vendedor/Estado: venta_pagos no las tiene (quitadas A-T3,
 * antes renderizaban '-' fantasma). Filtros reales del endpoint: period/from/to +
 * medio + groupBy + page/pageSize.
 *
 * Dependencias:
 *   - FinanzasApiClient.listIngresos / getIngresoById
 *   - BusinessSelector (getSelectedBusiness)
 *
 * @module presentation/views/IngresosView
 */

import { FinanzasApiClient } from '../../infrastructure/api/FinanzasApiClient.js';
import { getSelectedBusiness } from '../components/BusinessSelector.js';

const PERIODS = [
  { code: 'total',  label: 'Total' },
  { code: 'mes',    label: 'Mes' },
  { code: 'semana', label: 'Semana' },
  { code: 'hoy',    label: 'Hoy' },
  { code: 'rango',  label: 'Rango' }
];
const MEDIOS = [
  { code: '',              label: 'Todos' },
  { code: 'efectivo',      label: 'Efectivo' },
  { code: 'transferencia', label: 'Transferencia' }
];
const GROUP_MODES = [
  { code: 'venta', label: 'Por venta' },
  { code: 'pago',  label: 'Por pago' }
];
const PAGE_SIZE = 100;

const MEDIO_COLOR = {
  efectivo:      '#3fb950',
  transferencia: '#58a6ff',
  mixto:         '#d2a8ff'
};

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc as escHtml } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';
import { formatearFecha } from '../shared/fechaColombiana.js';

const formatCurrency = (n) => {
  const num = Number(n) || 0;
  return num.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

// fecha ISO ("2026-05-31T20:15:00") -> "2026-05-31 20:15"; date-only pasa tal cual.
// Paridad Ingresos<->Egresos (REDIRECT Josse #2157): fecha = solo YYYY-MM-DD;
// la hora vive en su propia columna (formatHora). venta_pagos.fecha es DATE en
// V1.5 (sin hora) -> la columna Hora de ingresos muestra "—"; si el Desktop
// algun dia guarda hora en ventas, aparece automatico via la parte time.
const formatFecha = (f) => (f ? String(f).slice(0, 10) : '');
const formatHora = (f) => {
  if (!f) return '—';
  const s = String(f);
  const m = (s.includes('T') ? s.slice(11) : s).match(/(\d{1,2}):(\d{2})/);
  if (!m) return '—';
  let hh = parseInt(m[1], 10);
  const mm = m[2];
  if (hh === 0 && mm === '00') return '—';
  const ampm = hh >= 12 ? 'p. m.' : 'a. m.';
  hh = hh % 12; if (hh === 0) hh = 12;
  return `${hh}:${mm} ${ampm}`;
};

export async function renderIngresos(container) {
  const state = {
    items: [],
    totales: { ingresoTotal: 0 },
    porMedio: {},
    totalCount: 0,
    // A-T1: default 'total' (no 'mes'). La data real de ventas es de mayo (Exito Chino,
    // venta_pagos #1911); con default 'mes'=junio la vista saldria vacia.
    filters: { period: 'total', medio: '', from: '', to: '' },
    groupBy: 'venta',
    page: 1,
    expandedId: null,
    expandedDetail: null,
    loading: false,
    error: null
  };

  container.innerHTML = `
    <section class="artifact-view ingresos-view">
      <div class="artifact-header">
        <div>
          ${regresoHtml()}
          <h1>💰 Ingresos</h1>
          <p style="margin:4px 0 0;color:#8b949e;font-size:0.875rem">
            Ventas registradas — paridad Desktop FrmIngresosV15
          </p>
        </div>
        <div data-iv-poll style="font-size:0.875rem;color:#8b949e">⟳ cargando…</div>
      </div>

      <form data-iv-filters class="ingresos-filters"
            style="display:flex;flex-wrap:wrap;gap:10px;align-items:flex-end;margin-top:14px;
                   padding:12px;background:#161b22;border:1px solid #30363d;border-radius:8px">
        ${renderFilterFields(state.filters)}
        <div data-iv-groupby style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
          Vista
          <div style="display:flex;border:1px solid #30363d;border-radius:6px;overflow:hidden">
            ${GROUP_MODES.map(g => `
              <button type="button" data-iv-mode="${g.code}"
                      style="padding:6px 12px;border:0;cursor:pointer;font-size:0.875rem;
                             background:${g.code === state.groupBy ? '#1f6feb' : '#0d1117'};
                             color:${g.code === state.groupBy ? '#fff' : '#8b949e'}">${g.label}</button>
            `).join('')}
          </div>
        </div>
        <button type="submit" class="btn-primary btn-sm">Aplicar</button>
        <button type="button" data-iv-reset class="btn-ghost btn-sm">Limpiar</button>
      </form>

      <div data-iv-totales class="ingresos-totales"
           style="display:flex;gap:14px;margin-top:14px;flex-wrap:wrap"></div>

      <div data-iv-tablewrap style="margin-top:14px;border:1px solid #30363d;border-radius:8px;overflow:hidden;background:#0d1117">
        <table style="width:100%;border-collapse:collapse;font-size:0.875rem">
          <thead style="background:#161b22" data-iv-thead></thead>
          <tbody data-iv-tbody></tbody>
        </table>
      </div>

      <div data-iv-pager style="display:flex;gap:10px;align-items:center;justify-content:flex-end;margin-top:10px"></div>
    </section>
  `;

  // ============== Wiring ==============

  const $form = container.querySelector('[data-iv-filters]');
  const $reset = container.querySelector('[data-iv-reset]');
  const $thead = container.querySelector('[data-iv-thead]');
  const $tbody = container.querySelector('[data-iv-tbody]');
  const $totales = container.querySelector('[data-iv-totales]');
  const $poll = container.querySelector('[data-iv-poll]');
  const $pager = container.querySelector('[data-iv-pager]');

  $form.addEventListener('change', (e) => {
    if (e.target.name === 'period') updatePeriodVisibility(container, e.target.value);
  });
  $form.addEventListener('submit', (e) => {
    e.preventDefault();
    Object.assign(state.filters, readFilters(container));
    state.page = 1;
    loadList();
  });
  $reset.addEventListener('click', () => {
    state.filters = { period: 'total', medio: '', from: '', to: '' };
    state.page = 1;
    $form.reset();
    container.querySelectorAll('[name="period"]').forEach(el => { el.value = 'total'; });
    updatePeriodVisibility(container, 'total');
    loadList();
  });
  container.querySelectorAll('[data-iv-mode]').forEach(btn => {
    btn.addEventListener('click', () => {
      const mode = btn.dataset.ivMode;
      if (mode === state.groupBy) return;
      state.groupBy = mode;
      state.page = 1;
      state.expandedId = null;
      state.expandedDetail = null;
      container.querySelectorAll('[data-iv-mode]').forEach(b => {
        const active = b.dataset.ivMode === mode;
        b.style.background = active ? '#1f6feb' : '#0d1117';
        b.style.color = active ? '#fff' : '#8b949e';
      });
      loadList();
    });
  });

  await loadList();

  // ============== Funciones internas ==============

  async function loadList() {
    const businessId = getSelectedBusiness();
    renderHead();
    if (!businessId) {
      $tbody.innerHTML = `<tr><td colspan="${colCount()}" style="padding:20px;text-align:center;color:#d29922">Selecciona un negocio en la barra superior</td></tr>`;
      $totales.innerHTML = '';
      $pager.innerHTML = '';
      $poll.textContent = '⚠ sin negocio';
      return;
    }
    state.loading = true;
    $poll.textContent = '⟳ cargando…';
    const res = await FinanzasApiClient.listIngresos(businessId, {
      ...state.filters,
      groupBy: state.groupBy,
      page: state.page,
      pageSize: PAGE_SIZE
    });
    state.loading = false;
    if (!res.ok) {
      state.error = res.error;
      $tbody.innerHTML = `<tr><td colspan="${colCount()}" style="padding:20px;text-align:center;color:#f85149">Error: ${escHtml(JSON.stringify(res.error || {}).slice(0, 120))}</td></tr>`;
      $poll.textContent = '⚠ error';
      return;
    }
    const payload = res.data?.data ?? res.data ?? {};
    state.items = Array.isArray(payload.items) ? payload.items : [];
    state.totales = payload.totales || { ingresoTotal: sumMontos(state.items) };
    state.porMedio = payload.porMedio || {};
    state.totalCount = Number(payload.totalCount) || state.items.length;
    state.error = null;
    renderRows();
    renderTotales(payload._hint);
    renderPager();
    const unidad = state.groupBy === 'venta' ? 'factura(s)' : 'pago(s)';
    $poll.textContent = `✓ ${state.totalCount} ${unidad} · ${formatearFecha(new Date(), {}, { parte: 'hora' })}`;
  }

  function colCount() {
    return state.groupBy === 'venta' ? 5 : 6;
  }

  function renderHead() {
    const th = (label, right = false) =>
      `<th style="padding:8px 10px;text-align:${right ? 'right' : 'left'};color:#8b949e;font-size:0.875rem;text-transform:uppercase;border-bottom:1px solid #30363d">${label}</th>`;
    // REDIRECT #2157: orden paritario con Egresos -> Fecha | Hora | ... | Total.
    // (antes Factura#|Fecha|Medio|Total). Espejo FrmIngresosV15 + columna Hora.
    $thead.innerHTML = state.groupBy === 'venta'
      ? `<tr>${th('Fecha')}${th('Hora')}${th('Factura #')}${th('Medio')}${th('Total', true)}</tr>`
      : `<tr>${th('Fecha')}${th('Hora')}${th('Factura #')}${th('Medio')}${th('Origen')}${th('Monto', true)}</tr>`;
  }

  function renderRows() {
    if (state.items.length === 0) {
      $tbody.innerHTML = `<tr><td colspan="${colCount()}" style="padding:24px;text-align:center;color:#8b949e">
        Sin ingresos para los filtros aplicados.
      </td></tr>`;
      return;
    }
    $tbody.innerHTML = state.items.map((it, idx) => renderRow(it, idx)).join('');
    if (state.groupBy !== 'pago') return;
    // Expand solo en modo pago: el agregado por venta no tiene id consultable.
    $tbody.querySelectorAll('[data-iv-row-id]').forEach(tr => {
      tr.addEventListener('click', async () => {
        const id = tr.dataset.ivRowId;
        if (state.expandedId === id) {
          state.expandedId = null;
          state.expandedDetail = null;
          renderRows();
          return;
        }
        state.expandedId = id;
        state.expandedDetail = { loading: true };
        renderRows();
        const businessId = getSelectedBusiness();
        const res = await FinanzasApiClient.getIngresoById(businessId, id);
        state.expandedDetail = res.ok
          ? (res.data?.data ?? res.data ?? null)
          : { error: res.error };
        renderRows();
      });
    });
  }

  // ING-FIX-P (#2209, contrato #2204): mostrar el numero de FACTURA real, no el
  // desktop_venta_id renumerado. Migrados Access->SQLite: numero_factura = 80XXX
  // (legacy_access_id, la factura real V1.0). Nativos V1.5: su id propio.
  // Creditos: null. Fallback a venta_id (246XXX) mientras ING-FIX-A (Argy-A)
  // despliega el campo numero_factura en el endpoint -> se enciende solo al llegar.
  function facturaDe(it) {
    return it.numero_factura ?? it.numeroFactura ?? it.venta_id ?? it.ventaId;
  }
  function renderFactura(num) {
    return num == null
      ? `<span style="color:#6e7681;font-style:italic" title="Pago sin venta vinculada (pago multiple transitorio, se resuelve en F3)">— sin venta</span>`
      : `<span style="color:#79c0ff;font-family:monospace">${escHtml(num)}</span>`;
  }

  function renderMedio(it) {
    const medio = (it.medio || '-').toLowerCase();
    const color = MEDIO_COLOR[medio] || '#8b949e';
    const pagos = Number(it.num_pagos ?? it.numPagos) || 0;
    const extra = pagos > 1 ? ` · ${pagos} pagos` : '';
    return `<span style="background:${color}22;color:${color};padding:2px 8px;border-radius:10px;font-size:0.875rem;font-weight:600">${escHtml(medio)}${extra}</span>`;
  }

  function renderRow(it, idx) {
    const fechaRaw = it.fecha ?? it.fechaOperativa;
    const fecha = formatFecha(fechaRaw);
    const hora = formatHora(fechaRaw); // venta_pagos.fecha es DATE -> "—" (paridad con Egresos)
    const total = formatCurrency(it.monto ?? it.total ?? 0);
    const tdBase = 'padding:8px 10px;color:#c9d1d9';
    const tdHora = 'padding:8px 10px;color:#8b949e;font-size:0.875rem';
    if (state.groupBy === 'venta') {
      return `
        <tr style="border-bottom:1px solid #21262d">
          <td style="${tdBase}">${escHtml(fecha)}</td>
          <td style="${tdHora}">${escHtml(hora)}</td>
          <td style="${tdBase}">${renderFactura(facturaDe(it))}</td>
          <td style="padding:8px 10px">${renderMedio(it)}</td>
          <td style="padding:8px 10px;text-align:right;color:#3fb950;font-weight:600">$${total}</td>
        </tr>
      `;
    }
    const isExpanded = state.expandedId === it.id;
    return `
      <tr data-iv-row-id="${escHtml(it.id)}"
          style="cursor:pointer;border-bottom:1px solid #21262d;background:${isExpanded ? '#1c2530' : 'transparent'}"
          onmouseover="this.style.background='${isExpanded ? '#1c2530' : '#161b22'}'"
          onmouseout="this.style.background='${isExpanded ? '#1c2530' : 'transparent'}'">
        <td style="${tdBase}">${escHtml(fecha)}</td>
        <td style="${tdHora}">${escHtml(hora)}</td>
        <td style="${tdBase}">${renderFactura(facturaDe(it))}</td>
        <td style="padding:8px 10px">${renderMedio(it)}</td>
        <td style="${tdBase}">${escHtml(it.origen || '-')}</td>
        <td style="padding:8px 10px;text-align:right;color:#3fb950;font-weight:600">$${total}</td>
      </tr>
      ${isExpanded ? `<tr><td colspan="6" style="padding:0;background:#0d1117;border-bottom:1px solid #21262d">
        ${renderDetail(state.expandedDetail)}
      </td></tr>` : ''}
    `;
  }

  function renderDetail(detail) {
    if (!detail) return '<div style="padding:14px;color:#8b949e">Cargando…</div>';
    if (detail.loading) return '<div style="padding:14px;color:#8b949e">⟳ Cargando detalle…</div>';
    if (detail.error) return `<div style="padding:14px;color:#f85149">Error: ${escHtml(JSON.stringify(detail.error).slice(0, 200))}</div>`;
    // Detalle honesto del pago (venta_pagos): antes se renderizaba una tabla de
    // "items de factura" que el endpoint nunca devuelve (salia vacia siempre).
    const campo = (label, value, mono = false) => `
      <div style="min-width:160px">
        <div style="font-size:0.875rem;color:#8b949e;text-transform:uppercase">${label}</div>
        <div style="color:#c9d1d9;font-size:0.875rem;margin-top:2px;${mono ? 'font-family:monospace' : ''}">${escHtml(value ?? '-')}</div>
      </div>`;
    return `
      <div style="padding:14px">
        <h4 style="margin:0 0 10px;color:#f0f6fc;font-size:0.9rem">💳 Detalle del pago</h4>
        <div style="display:flex;gap:18px;flex-wrap:wrap">
          ${campo('Monto', '$' + formatCurrency(detail.monto))}
          ${campo('Medio', detail.medio)}
          ${campo('Fecha', formatFecha(detail.fecha))}
          ${campo('Factura #', facturaDe(detail) ?? '— sin venta')}
          ${campo('Origen', detail.origen)}
          ${campo('Sync ID', detail.sync_id ?? detail.syncId, true)}
        </div>
      </div>
    `;
  }

  function renderTotales(hint) {
    const total = Number(state.totales.ingresoTotal ?? state.totales.totalIngresos ?? 0);
    const medios = Object.entries(state.porMedio)
      .sort(([a], [b]) => a.localeCompare(b));
    const card = (label, value, color) => `
      <div style="flex:1;min-width:160px;padding:12px;background:#161b22;border:1px solid #30363d;border-left:3px solid ${color};border-radius:6px">
        <div style="font-size:0.875rem;color:#8b949e;text-transform:uppercase">${escHtml(label)}</div>
        <div style="font-size:1.2rem;color:${color};font-weight:700;margin-top:4px">$${formatCurrency(value)}</div>
      </div>`;
    $totales.innerHTML = `
      ${card('Total Ingresos', total, '#3fb950')}
      ${medios.map(([medio, monto]) => card(medio, monto, MEDIO_COLOR[medio] || '#58a6ff')).join('')}
      ${hint ? `<div style="flex:1;min-width:240px;padding:10px;background:#1f6feb22;border:1px dashed #1f6feb;border-radius:6px;color:#79c0ff;font-size:0.875rem">ℹ ${escHtml(hint)}</div>` : ''}
    `;
  }

  function renderPager() {
    const totalPages = Math.max(1, Math.ceil(state.totalCount / PAGE_SIZE));
    if (totalPages <= 1) { $pager.innerHTML = ''; return; }
    $pager.innerHTML = `
      <button type="button" data-iv-prev class="btn-ghost btn-sm" ${state.page <= 1 ? 'disabled' : ''}>&lsaquo; Anterior</button>
      <span style="font-size:0.875rem;color:#8b949e">Página ${state.page} de ${totalPages}</span>
      <button type="button" data-iv-next class="btn-ghost btn-sm" ${state.page >= totalPages ? 'disabled' : ''}>Siguiente &rsaquo;</button>
    `;
    $pager.querySelector('[data-iv-prev]')?.addEventListener('click', () => {
      if (state.page > 1) { state.page--; loadList(); }
    });
    $pager.querySelector('[data-iv-next]')?.addEventListener('click', () => {
      if (state.page < totalPages) { state.page++; loadList(); }
    });
  }
}

// ============== Helpers compartidos ==============

function renderFilterFields(filters) {
  const periodOpts = PERIODS.map(p => `<option value="${p.code}" ${p.code === filters.period ? 'selected' : ''}>${p.label}</option>`).join('');
  const medioOpts = MEDIOS.map(m => `<option value="${m.code}" ${m.code === filters.medio ? 'selected' : ''}>${m.label}</option>`).join('');
  const rangoVisible = filters.period === 'rango';
  return `
    <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
      Periodo
      <select name="period" class="form-input form-input-sm" style="min-width:120px;font-size:14px">${periodOpts}</select>
    </label>
    <label data-iv-rango-from style="display:${rangoVisible ? 'flex' : 'none'};flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
      Fecha ini
      <input type="date" name="from" class="form-input form-input-sm" value="${filters.from || ''}"/>
    </label>
    <label data-iv-rango-to style="display:${rangoVisible ? 'flex' : 'none'};flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
      Fecha fin
      <input type="date" name="to" class="form-input form-input-sm" value="${filters.to || ''}"/>
    </label>
    <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
      Medio
      <select name="medio" class="form-input form-input-sm" style="min-width:130px;font-size:14px">${medioOpts}</select>
    </label>
  `;
}

function readFilters(container) {
  const $form = container.querySelector('[data-iv-filters]');
  return {
    period: $form.querySelector('[name="period"]')?.value || 'total',
    medio:  $form.querySelector('[name="medio"]')?.value || '',
    from:   $form.querySelector('[name="from"]')?.value || '',
    to:     $form.querySelector('[name="to"]')?.value || ''
  };
}

function updatePeriodVisibility(container, period) {
  const isRango = period === 'rango';
  const $from = container.querySelector('[data-iv-rango-from]');
  const $to = container.querySelector('[data-iv-rango-to]');
  if ($from) $from.style.display = isRango ? 'flex' : 'none';
  if ($to) $to.style.display = isRango ? 'flex' : 'none';
}

function sumMontos(items) {
  return items.reduce((s, it) => s + (Number(it.monto ?? it.total) || 0), 0);
}
